import React, { useEffect, useMemo, useRef, useState } from "react";
import { FieldProps, GeoPoint } from "@firecms/core";
import { TextField, Button } from "@firecms/ui";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Vite bundling breaks Leaflet's default icon URL resolution
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

const US_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const PICKED_ZOOM = 9;

function MapController({ position }: { position: [number, number] | null }) {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.setView(position, Math.max(map.getZoom(), PICKED_ZOOM));
        }
    }, [position?.[0], position?.[1]]);
    return null;
}

function ClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
    useMapEvents({
        click: (e) => onPick(e.latlng.lat, e.latlng.lng)
    });
    return null;
}

export function LatLngField({
    property,
    value,
    setValue,
    showError,
    error
}: FieldProps<GeoPoint>) {
    const lat = typeof value?.latitude === "number" ? value.latitude : undefined;
    const lng = typeof value?.longitude === "number" ? value.longitude : undefined;

    const [latText, setLatText] = useState<string>(lat !== undefined ? String(lat) : "");
    const [lngText, setLngText] = useState<string>(lng !== undefined ? String(lng) : "");
    const [searchText, setSearchText] = useState("");
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const lastCommitted = useRef<string>(`${lat ?? ""},${lng ?? ""}`);

    // Sync inputs when the value changes from outside (map click, drag, search)
    useEffect(() => {
        const key = `${lat ?? ""},${lng ?? ""}`;
        if (key !== lastCommitted.current) {
            lastCommitted.current = key;
            setLatText(lat !== undefined ? String(lat) : "");
            setLngText(lng !== undefined ? String(lng) : "");
        }
    }, [lat, lng]);

    const commit = (nextLatText: string, nextLngText: string) => {
        const parsedLat = nextLatText.trim() === "" ? undefined : Number(nextLatText);
        const parsedLng = nextLngText.trim() === "" ? undefined : Number(nextLngText);
        const latValid = parsedLat !== undefined && !Number.isNaN(parsedLat);
        const lngValid = parsedLng !== undefined && !Number.isNaN(parsedLng);
        if (latValid && lngValid) {
            lastCommitted.current = `${parsedLat},${parsedLng}`;
            setValue(new GeoPoint(parsedLat, parsedLng));
        } else {
            // Incomplete or unparsable pair: clear the form value so the
            // required-field validation blocks saving stale coordinates.
            // lastCommitted stays in sync so the effect doesn't wipe the
            // user's in-progress text.
            lastCommitted.current = ",";
            setValue(null);
        }
    };

    const setFromMap = (newLat: number, newLng: number) => {
        const roundedLat = Math.round(newLat * 1e6) / 1e6;
        const roundedLng = Math.round(newLng * 1e6) / 1e6;
        lastCommitted.current = `${roundedLat},${roundedLng}`;
        setLatText(String(roundedLat));
        setLngText(String(roundedLng));
        setValue(new GeoPoint(roundedLat, roundedLng));
    };

    const runSearch = async () => {
        const q = searchText.trim();
        if (!q || searching) return;
        setSearching(true);
        setSearchError(null);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
                { headers: { Accept: "application/json" } }
            );
            const results = await res.json();
            if (Array.isArray(results) && results.length > 0) {
                setFromMap(parseFloat(results[0].lat), parseFloat(results[0].lon));
            } else {
                setSearchError(`No results for "${q}"`);
            }
        } catch (e) {
            setSearchError("Search failed, set the coordinates manually");
        } finally {
            setSearching(false);
        }
    };

    const parsedLat = latText.trim() === "" ? undefined : Number(latText);
    const parsedLng = lngText.trim() === "" ? undefined : Number(lngText);
    const latRangeError = parsedLat !== undefined && !Number.isNaN(parsedLat) && (parsedLat < -90 || parsedLat > 90)
        ? "Latitude must be between -90 and 90" : undefined;
    const lngRangeError = parsedLng !== undefined && !Number.isNaN(parsedLng) && (parsedLng < -180 || parsedLng > 180)
        ? "Longitude must be between -180 and 180" : undefined;
    const pairIncompleteError = (parsedLat === undefined) !== (parsedLng === undefined)
        ? "Latitude and longitude are both required" : undefined;

    const markerPosition: [number, number] | null = useMemo(() => {
        if (lat === undefined || lng === undefined) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return [lat, lng];
    }, [lat, lng]);

    const validationMessage = latRangeError || lngRangeError || pairIncompleteError
        || (showError && typeof error === "string" ? error : undefined);

    return (
        <div className="flex flex-col gap-2 p-4 border border-surface-200 dark:border-surface-800 rounded-md bg-surface-50 dark:bg-surface-900">
            <span className="text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                {property.name}
            </span>
            <div className="flex gap-2 items-center">
                <TextField
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            runSearch();
                        }
                    }}
                    placeholder="Search a place, e.g. Austin, TX"
                    size="small"
                    className="flex-grow"
                />
                <Button variant="outlined" size="small" onClick={runSearch} disabled={searching || !searchText.trim()}>
                    {searching ? "Searching..." : "Search"}
                </Button>
            </div>
            {searchError && (
                <span className="text-xs text-red-500">{searchError}</span>
            )}
            <div style={{ height: 280 }} className="rounded-md overflow-hidden">
                <MapContainer
                    center={markerPosition ?? US_CENTER}
                    zoom={markerPosition ? PICKED_ZOOM : DEFAULT_ZOOM}
                    style={{ height: "100%", width: "100%" }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <ClickHandler onPick={setFromMap} />
                    <MapController position={markerPosition} />
                    {markerPosition && (
                        <Marker
                            position={markerPosition}
                            draggable
                            eventHandlers={{
                                dragend: (e) => {
                                    const p = (e.target as L.Marker).getLatLng();
                                    setFromMap(p.lat, p.lng);
                                }
                            }}
                        />
                    )}
                </MapContainer>
            </div>
            <div className="flex gap-2">
                <TextField
                    value={latText}
                    onChange={(e) => {
                        setLatText(e.target.value);
                        commit(e.target.value, lngText);
                    }}
                    label="Latitude"
                    size="small"
                    type="number"
                    className="flex-1"
                />
                <TextField
                    value={lngText}
                    onChange={(e) => {
                        setLngText(e.target.value);
                        commit(latText, e.target.value);
                    }}
                    label="Longitude"
                    size="small"
                    type="number"
                    className="flex-1"
                />
            </div>
            {validationMessage && (
                <span className="text-xs text-red-500">{validationMessage}</span>
            )}
        </div>
    );
}
